import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notificationsAPI } from '../../services/api';

const initialState = {
  inventory: 0,
  tasks: 0,
  shop: 0,
  network: 0,
  battlePass: 0,

  details: {
    inventory: {
      openableChests: 0,
      totalChests: 0,
      totalKeys: 0,
      newModules: 0,
      totalModules: 0,
    },
  },

  lastUpdate: null,
  isLoading: false,
  error: null,
};

// Fetches notification badge counts from REST API (GET /v1/notifications).
// Called on app init and re-dispatched after actions that can change a badge
// (task claim, spin, chest open, BattlePass claim, route change).
// WebSocket events (counter.updated / counters.updated) carry the same numbers.
export const fetchNotifications = createAsyncThunk(
  'counters/fetchNotifications',
  async (_, { rejectWithValue }) => {
    try {
      return await notificationsAPI.getNotifications();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const countersSlice = createSlice({
  name: 'counters',
  initialState,
  reducers: {
    // WebSocket full push: replaces all counters at once
    updateFromWebSocket: (state, action) => {
      const { inventory, tasks, shop, network, battlePass, details, lastUpdate } = action.payload;
      if (inventory !== undefined) state.inventory = inventory;
      if (tasks !== undefined) state.tasks = tasks;
      if (shop !== undefined) state.shop = shop;
      if (network !== undefined) state.network = network;
      if (battlePass !== undefined) state.battlePass = battlePass;
      if (details !== undefined) state.details = details;
      state.lastUpdate = lastUpdate || new Date().toISOString();
    },
    // WebSocket single counter push
    updateSingleCounter: (state, action) => {
      const { type, count } = action.payload;
      if (type in state) state[type] = count;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        const { tasks, inventory, network, shop, battlePass, details } = action.payload || {};
        state.tasks = tasks ?? state.tasks;
        state.inventory = inventory ?? state.inventory;
        state.network = network ?? state.network;
        state.shop = shop ?? state.shop;
        state.battlePass = battlePass ?? state.battlePass;
        if (details?.inventory) {
          state.details.inventory = { ...state.details.inventory, ...details.inventory };
        }
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { updateFromWebSocket, updateSingleCounter, clearError } = countersSlice.actions;
export default countersSlice.reducer;
